/**
 * DiagramsPanel — surfaces engineering drawings + figures the
 * text-only parse silently dropped (P&IDs, single-line diagrams,
 * GA drawings, schematics, plot plans, photo-pages).
 *
 * Backed by:
 *   GET  /rfp/{id}/diagrams                — persisted descriptions
 *   POST /rfp/{id}/diagrams/extract        — runs Gemini multimodal
 *   GET  /rfp/{id}/page/{p}/image          — pre-existing PNG render
 *
 * Vision tokens cost ~5x text on Gemini Flash, so extraction is
 * explicitly user-triggered (button) rather than running in the
 * bulk parse. Per-page errors are isolated — one bad page doesn't
 * blow the whole batch.
 */
import React, { useCallback, useEffect, useState } from "react";
import { v2 } from "@/lib/v2Api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Image as ImageIcon, Loader2, RefreshCcw, AlertCircle, Sparkles, ExternalLink,
} from "lucide-react";

const KIND_LABEL = {
  p_and_id: "P&ID",
  single_line_diagram: "Single-line Diagram",
  ga_drawing: "GA Drawing",
  floor_plan: "Floor Plan",
  schematic: "Schematic",
  specs_table: "Spec Table",
  cover_page: "Cover Page",
  flowchart: "Flowchart",
  photo: "Photograph",
  text_only: "Text-only",
  other: "Other",
};

const KIND_COLOR = {
  p_and_id: "bg-violet-100 text-violet-700",
  single_line_diagram: "bg-blue-100 text-blue-700",
  ga_drawing: "bg-cyan-100 text-cyan-700",
  floor_plan: "bg-teal-100 text-teal-700",
  schematic: "bg-indigo-100 text-indigo-700",
  specs_table: "bg-amber-100 text-amber-700",
  flowchart: "bg-emerald-100 text-emerald-700",
  cover_page: "bg-slate-100 text-slate-600",
  text_only: "bg-slate-100 text-slate-500",
  photo: "bg-rose-100 text-rose-700",
  other: "bg-slate-100 text-slate-700",
};

function DiagramCard({ d, rfpId, backendUrl }) {
  const tone = KIND_COLOR[d.kind] || KIND_COLOR.other;
  const label = KIND_LABEL[d.kind] || d.kind;
  const imgSrc = `${backendUrl}/api/v2/rfp/${rfpId}/page/${d.page}/image`;
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              {d.title || `Page ${d.page}`}
              <Badge className={tone}>{label}</Badge>
              {!d.is_substantive && (
                <Badge className="bg-slate-100 text-slate-500">non-substantive</Badge>
              )}
            </CardTitle>
            <div className="text-xs text-slate-500 mt-0.5">
              Page {d.page}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <a
            href={imgSrc}
            target="_blank"
            rel="noreferrer"
            className="border border-slate-200 rounded overflow-hidden block hover:border-blue-300 transition"
            title="Open full-size page image"
          >
            <img
              src={imgSrc}
              alt={`Page ${d.page}`}
              className="w-full h-auto block bg-white"
              loading="lazy"
            />
          </a>
          <div className="space-y-3">
            {d.error ? (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                <AlertCircle className="w-4 h-4 inline mr-1.5" />
                {d.error}
              </div>
            ) : (
              <>
                {d.caption && (
                  <p className="text-sm text-slate-700 leading-relaxed">{d.caption}</p>
                )}
                {(d.components || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                      Components
                    </div>
                    <div className="space-y-1">
                      {d.components.map((c, i) => (
                        <div key={i} className="text-xs flex items-center gap-2">
                          <code className="bg-slate-100 px-1.5 py-0.5 rounded">{c.label}</code>
                          {c.kind && c.kind !== "other" && (
                            <span className="text-slate-500">{c.kind}</span>
                          )}
                          {c.rating_or_spec && (
                            <span className="text-slate-700">· {c.rating_or_spec}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(d.notes_inline || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                      Inline notes
                    </div>
                    <ul className="text-xs text-slate-700 list-disc list-inside space-y-0.5">
                      {d.notes_inline.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
            <a
              href={imgSrc}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              Open full page <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DiagramsPanel({ rfpId }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const [diagrams, setDiagrams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [maxPages, setMaxPages] = useState(8);

  const load = useCallback(async () => {
    if (!rfpId) return;
    setLoading(true);
    try {
      const r = await v2.get(`/rfp/${rfpId}/diagrams`);
      setDiagrams(r.data?.diagrams || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load diagrams");
    } finally {
      setLoading(false);
    }
  }, [rfpId]);

  useEffect(() => { load(); }, [load]);

  const runExtract = async () => {
    setExtracting(true);
    try {
      const r = await v2.post(
        `/rfp/${rfpId}/diagrams/extract`,
        { max_auto_pages: maxPages },
        // Vision calls take ~5-15s per page. Generous timeout.
        { timeout: 300000 },
      );
      setDiagrams(r.data?.diagrams || []);
      if (r.data?.warning) {
        toast.info(r.data.warning);
      } else {
        toast.success(
          `Extracted diagrams from ${r.data?.pages_processed?.length ?? 0} page(s)`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-[#E2E8F0]">
        <CardContent className="p-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading diagrams…
        </CardContent>
      </Card>
    );
  }

  const substantive = diagrams.filter((d) => d.is_substantive && !d.error);
  const skipped = diagrams.filter((d) => !d.is_substantive || d.error);

  return (
    <div className="space-y-4">
      <Card className="border-[#E2E8F0]">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                AI-Extracted Diagrams
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                Surfaces P&IDs, single-line diagrams, GA drawings, schematics
                and spec tables the text parse silently dropped. Uses
                Gemini multimodal — costs ~5x a text-only call so this is
                an explicit user action.
              </p>
            </div>
            <div className="flex items-end gap-2 shrink-0">
              <label className="text-[10px] text-slate-500 flex flex-col">
                Max auto-detected pages
                <input
                  type="number" min="1" max="30"
                  value={maxPages}
                  onChange={(e) => setMaxPages(Math.max(1, parseInt(e.target.value || "8", 10)))}
                  disabled={extracting}
                  className="mt-0.5 w-20 px-2 py-1 border border-slate-300 rounded text-xs"
                />
              </label>
              <Button onClick={runExtract} disabled={extracting} size="sm">
                {extracting
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Extracting…</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1" /> {diagrams.length ? "Re-run extraction" : "Run extraction"}</>}
              </Button>
              {diagrams.length > 0 && (
                <Button variant="outline" size="sm" onClick={load} disabled={extracting}>
                  <RefreshCcw className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {diagrams.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-10 text-center text-slate-500 text-sm">
            No diagrams extracted yet. Click <b>Run extraction</b> above —
            we'll auto-pick the most figure-likely pages and describe
            each one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {substantive.map((d) => (
            <DiagramCard key={d.page} d={d} rfpId={rfpId} backendUrl={backendUrl} />
          ))}
          {skipped.length > 0 && (
            <details className="bg-slate-50 border border-slate-200 rounded p-3">
              <summary className="text-xs text-slate-600 cursor-pointer">
                {skipped.length} non-substantive / failed page{skipped.length === 1 ? "" : "s"}
              </summary>
              <div className="mt-2 space-y-2">
                {skipped.map((d) => (
                  <DiagramCard key={d.page} d={d} rfpId={rfpId} backendUrl={backendUrl} />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
